/* Reusable design-prototype pixel background, scoped to its parent component. */
if (!customElements.get('bf-pixel-field')) {
  class BFPixelField extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      this.surface = this.dataset.surface ? this.closest(this.dataset.surface) : this.parentElement;
      if (!this.surface) return;
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas?.getContext('2d');
      if (!this.context) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.cells = [];
      this.waves = [];
      this.lastWave = 0;
      this.visible = false;
      this.surface.addEventListener('pointermove', (event) => {
        if (!this.animated()) return;
        const now = Date.now();
        if (now - this.lastWave < 70) return;
        const rect = this.surface.getBoundingClientRect();
        this.waves.push({ x: event.clientX - rect.left, y: event.clientY - rect.top, start: now });
        if (this.waves.length > 12) this.waves.shift();
        this.lastWave = now;
      }, { signal, passive: true });
      this.motion.addEventListener('change', () => { this.draw(); this.schedule(); }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.surface);
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.schedule();
      }, { rootMargin: '250px' });
      this.visibilityObserver.observe(this.surface);
      this.resize();
    }
    animated() { return this.dataset.animate !== 'false' && !this.motion.matches; }
    chance(column, row, time) {
      const flow = Math.sin(column * .045 + row * .06 + time * .28) * Math.cos(column * .025 - row * .04 - time * .16);
      return (.015 + Math.max(0, (flow - .08) / .92) * .55) * .5;
    }
    resize() {
      const width = this.surface.clientWidth;
      const height = this.surface.clientHeight;
      if (this.canvas.width === width && this.canvas.height === height && this.cells.length) return;
      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
      this.columns = Math.max(1, Math.ceil(width / 11));
      this.cells = Array.from({ length: this.columns * Math.max(1, Math.ceil(height / 11)) }, (_, index) =>
        Math.random() < this.chance(index % this.columns, Math.floor(index / this.columns), 0) ? 1 : 0);
      this.draw();
    }
    draw() {
      const now = Date.now();
      const animated = this.animated();
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.context.fillStyle = '#212121';
      this.waves = animated ? this.waves.filter((wave) => now - wave.start < 1500) : [];
      if (animated) {
        for (let i = 0; i < Math.max(1, Math.floor(this.cells.length * .07)); i++) {
          const index = Math.floor(Math.random() * this.cells.length);
          this.cells[index] = Math.random() < this.chance(index % this.columns, Math.floor(index / this.columns), now / 1000) ? 1 : 0;
        }
      }
      this.cells.forEach((cell, index) => {
        const x = index % this.columns * 11;
        const y = Math.floor(index / this.columns) * 11;
        let ripple = 0;
        this.waves.forEach((wave) => {
          const age = (now - wave.start) / 1000;
          const distance = Math.hypot(x - wave.x, y - wave.y);
          ripple = Math.max(ripple, Math.max(0, 1 - Math.abs(distance - age * 250) / 65) * (1 - age / 1.5));
        });
        if (cell || (animated && Math.random() < ripple * .375)) this.context.fillRect(x, y, 4, 4);
      });
    }
    schedule() {
      clearInterval(this.timer);
      if (this.visible && !document.hidden && this.animated()) this.timer = setInterval(() => this.draw(), 140);
    }
    disconnectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      clearInterval(this.timer);
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.waves = [];
    }
  }
  customElements.define('bf-pixel-field', BFPixelField);
}
